'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Sidebar from './Sidebar';
import AppTopbar from './AppTopbar';
import MiniPlayer from './MiniPlayer';
import { Suspense } from 'react';

interface AppLayoutProps {
  children: React.ReactNode;
  activeRoute?: string;
  requiredRole?: 'user' | 'artist' | 'admin';
}

export default function AppLayout({ children, activeRoute, requiredRole }: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [roleVerified, setRoleVerified] = useState(false);
  const { user, authReady } = useAuth();
  const router = useRouter();

  useEffect(() => {
    // Wait for AuthContext to fully resolve before any routing decisions
    if (!authReady) return;

    if (!user) {
      router.replace('/sign-up-login-screen');
      return;
    }

    if (!requiredRole) {
      setRoleVerified(true);
      return;
    }

    // Admin routes: AppLayout does NOT handle admin role checks.
    // Admin pages manage their own auth via AuthContext directly.
    // This prevents competing redirect logic.
    if (requiredRole === 'admin') {
      setRoleVerified(true);
      return;
    }

    // For non-admin roles: simple metadata check
    const role: string = (user?.user_metadata?.role as string) || 'user';
    if (requiredRole !== role) {
      const fallback = role === 'artist' ? '/artist-dashboard' : '/user-dashboard';
      router.replace(fallback);
      return;
    }

    setRoleVerified(true);
  }, [user, authReady, requiredRole, router]);

  if (!authReady || !roleVerified) {
    return (
      <div className="flex h-screen items-center justify-center" style={{ background: 'var(--background)' }}>
        <div className="flex flex-col items-center gap-3">
          <div
            className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: 'var(--secondary)', borderTopColor: 'transparent' }}
          />
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--background)' }}>
      {/* Mobile overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 z-50 lg:relative lg:z-auto
          sidebar-transition flex-shrink-0
          ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{ width: sidebarCollapsed ? '64px' : '240px' }}
      >
        <Suspense fallback={<div style={{ width: sidebarCollapsed ? '64px' : '240px' }} />}>
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            activeRoute={activeRoute}
            onCloseMobile={() => setMobileSidebarOpen(false)}
          />
        </Suspense>
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <AppTopbar onOpenMobileSidebar={() => setMobileSidebarOpen(true)} />
        <main className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 xl:p-8 pb-28">
          <div className="max-w-screen-2xl mx-auto">{children}</div>
        </main>
      </div>

      <MiniPlayer />
    </div>
  );
}